import { get, post, put, del } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import type { Tenant, CreateTenant, UpdateTenant, TenantEntitlements, Tier, BillingProvisionRequest, BillingProvisionResponse } from '@policy-tracker/types';

const API_NAME = 'PolicyTracker';

export interface PaginatedResponse<T> {
    items: T[];
    nextToken?: string;
}

/**
 * Get authorization headers for API requests
 */
async function getAuthHeaders() {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();

    if (!token) {
        throw new Error('No authentication token available');
    }

    return {
        Authorization: `Bearer ${token}`,
    };
}

export class TenantService {
    /**
     * Lists tenants with pagination
     */
    static async listTenants(params?: { limit?: number; nextToken?: string }): Promise<PaginatedResponse<Tenant>> {
        const queryParams = new URLSearchParams();

        if (params?.limit) {
            queryParams.append('limit', params.limit.toString());
        }

        if (params?.nextToken) {
            queryParams.append('nextToken', params.nextToken);
        }

        const authHeaders = await getAuthHeaders();

        const response = await get({
            apiName: API_NAME,
            path: `/api/tenants?${queryParams.toString()}`,
            options: {
                headers: authHeaders,
            },
        }).response;

        const data = (await response.body.json()) as unknown as PaginatedResponse<Tenant>;
        return data;
    }

    /**
     * Get a specific tenant by ID
     */
    static async getTenant(id: string): Promise<Tenant> {
        const authHeaders = await getAuthHeaders();

        const response = await get({
            apiName: API_NAME,
            path: `/api/tenants/${id}`,
            options: {
                headers: authHeaders,
            },
        }).response;

        const data = (await response.body.json()) as unknown as Tenant;
        return data;
    }

    /**
     * Create a new tenant
     */
    static async createTenant(tenant: CreateTenant): Promise<Tenant> {
        const authHeaders = await getAuthHeaders();

        const response = await post({
            apiName: API_NAME,
            path: '/api/tenants',
            options: {
                body: tenant,
                headers: authHeaders,
            },
        }).response;

        const data = (await response.body.json()) as unknown as Tenant;
        return data;
    }

    /**
     * Update an existing tenant
     */
    static async updateTenant(id: string, tenant: UpdateTenant): Promise<Tenant> {
        const authHeaders = await getAuthHeaders();

        const response = await put({
            apiName: API_NAME,
            path: `/api/tenants/${id}`,
            options: {
                body: tenant,
                headers: authHeaders,
            },
        }).response;

        const data = (await response.body.json()) as unknown as Tenant;
        return data;
    }

    /**
     * Delete a tenant
     */
    static async deleteTenant(id: string): Promise<void> {
        const authHeaders = await getAuthHeaders();

        await del({
            apiName: API_NAME,
            path: `/api/tenants/${id}`,
            options: {
                headers: authHeaders,
            },
        }).response;
    }

    /**
     * Get entitlements (including tier) for a tenant
     */
    static async getEntitlements(tenantId: string): Promise<TenantEntitlements> {
        const authHeaders = await getAuthHeaders();

        const response = await get({
            apiName: API_NAME,
            path: `/api/tenants/${tenantId}/entitlements`,
            options: {
                headers: authHeaders,
            },
        }).response;

        const data = (await response.body.json()) as unknown as TenantEntitlements;
        return data;
    }

    /**
     * Provision Stripe billing (customer and optionally subscription) for a tenant.
     *
     * Calls the TM admin endpoint added in #2071 to back-fill a Stripe customer
     * (and, by default, a subscription at the tenant's current tier price) for
     * tenants that never received them via the normal checkout flow. Used by
     * the TM Provision Billing action for legacy/pre-checkout tenants.
     *
     * @param tenantId - The tenant to provision billing for.
     * @param body - Request body controlling whether a subscription is created
     *   and which Stripe `collection_method` is used.
     * @returns The Stripe customer id, optional subscription id, and the
     *   `hasBillingAccount: true` flag now set on the tenant.
     */
    static async provisionBilling(tenantId: string, body: BillingProvisionRequest): Promise<BillingProvisionResponse> {
        const authHeaders = await getAuthHeaders();

        const response = await post({
            apiName: API_NAME,
            path: `/api/admin/tenants/${tenantId}/billing/provision`,
            options: {
                body,
                headers: authHeaders,
            },
        }).response;

        const data = (await response.body.json()) as unknown as BillingProvisionResponse;
        return data;
    }

    /**
     * Change the billing tier for a tenant
     */
    static async changeTier(tenantId: string, tier: Tier): Promise<void> {
        const authHeaders = await getAuthHeaders();

        await put({
            apiName: API_NAME,
            path: `/api/tenants/${tenantId}/tier`,
            options: {
                body: { tier },
                headers: authHeaders,
            },
        }).response;
    }
}

export default TenantService;
